import React, { useMemo, useState, useEffect } from "react";
import {
  MapPinIcon as MapPinOutline,
  ArrowPathIcon,
  CloudIcon,
} from "@heroicons/react/24/outline";
import { MapPinIcon as MapPinSolid } from "@heroicons/react/24/solid";
import {
  windIcon,
  sunIcon,
  pressureIcon,
  visibilityIcon,
  humidityIcon,
} from "../assets/dashboard-ui/icons.js";

import SunriseSunsetArc from "./SunriseSunsetArc.jsx";
import { glassCard } from "../styles/glass";

function buildWindPath(values, width, height) {
  if (!values?.length) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0.5);
  const padX = 8;
  const padY = 10;
  const n = values.length;
  const pts = values.map((v, i) => {
    const x = padX + (n === 1 ? 0 : (i / (n - 1)) * (width - padX * 2));
    const y = padY + (1 - (v - min) / span) * (height - padY * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return `M ${pts.join(" L ")}`;
}

function formatObsFooter(iso) {
  if (!iso || typeof iso !== "string") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function ApiMissingNote({ className = "" }) {
  return (
    <span className={`text-xs font-medium text-amber-300/95 ${className}`}>
      Not returned by API
    </span>
  );
}

function IconImg({ src, alt, className }) {
  return (
    <img
      src={src}
      alt={alt || ""}
      className={`shrink-0 object-contain ${className || ""}`}
    />
  );
}

export default function DashboardWeatherOverview({
  current,
  place,
  hourlyWindMph,
  tempHighF,
  tempLowF,
  sunrise,
  sunset,
  sunTimeZone,
  sunriseIso,
  sunsetIso,
  narrative,
  loading,
  error,
  getCurrentLocation,
  isCurrentLocation,
  isDefaultLocation,
  getWeatherIconUrl,
  refetchWeather,
}) {
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduceMotion(mq.matches);
    set();
    mq.addEventListener("change", set);
    return () => mq.removeEventListener("change", set);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = window.setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const windPath = useMemo(() => {
    const w = 320;
    const h = 72;
    return buildWindPath(hourlyWindMph, w, h);
  }, [hourlyWindMph]);

  const uvi =
    typeof current?.uvi === "number" && !Number.isNaN(current.uvi)
      ? current.uvi
      : null;
  const uvFillPct =
    uvi == null ? 0 : Math.min(100, (Math.min(uvi, 11) / 11) * 100);

  const humidity = current?.humidity;
  const pressure = current?.pressure;
  const windMph = current?.windSpeedMPH;
  const pop =
    typeof current?.pop === "number" && !Number.isNaN(current.pop)
      ? Math.round(current.pop)
      : null;

  const popHint =
    pop == null
      ? ""
      : pop === 0
        ? "No rain expected"
        : pop < 30
          ? "Low chance of rain"
          : pop < 60
            ? "Possible showers"
            : "Rain likely";
  const visMi =
    typeof current?.visibilityKM === "number"
      ? current.visibilityKM * 0.621371
      : null;

  const conditionLabel =
    current?.weather?.[0]?.description || current?.weatherPrimary || "";
  const iconCode = current?.weather?.[0]?.icon;
  const iconUrl =
    iconCode && getWeatherIconUrl ? getWeatherIconUrl(iconCode) : null;
  const tempF =
    typeof current?.tempF === "number" ? Math.round(current.tempF) : null;

  const locationTitle = [place?.name, place?.state].filter(Boolean).join(", ");
  const locationSub = [place?.country].filter(Boolean).join("");

  const obsFooter = formatObsFooter(current?.dateTimeISO);

  return (
    <div>
      {loading ? (
        <div className="grid animate-pulse grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
          <div className="h-64 rounded-xl bg-white/5 lg:col-span-7" />
          <div className="h-64 rounded-xl bg-white/5 lg:col-span-5" />
          <div className="h-40 rounded-xl bg-white/5 lg:col-span-6" />
          <div className="h-40 rounded-xl bg-white/5 lg:col-span-6" />
          <div className="h-28 rounded-xl bg-white/5 lg:col-span-3" />
          <div className="h-28 rounded-xl bg-white/5 lg:col-span-3" />
          <div className="h-28 rounded-xl bg-white/5 lg:col-span-3" />
          <div className="h-28 rounded-xl bg-white/5 lg:col-span-3" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-300">{error}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:gap-5">
          {/* Current weather */}
          <div className="flex min-w-0 flex-col rounded-xl p-4 shadow-sm backdrop-blur-md sm:col-span-2 sm:p-6 lg:col-span-7 lg:p-8">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h1 className="text-3xl tracking-tight text-white sm:text-5xl">
                  Current Weather
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-200 sm:text-[15px]">
                  {narrative ||
                    `${conditionLabel || "Conditions"} at your location.`}
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-borderGray bg-primary/50 px-1 py-1">
                {typeof refetchWeather === "function" && (
                  <button
                    type="button"
                    onClick={() => refetchWeather()}
                    className="rounded-full p-1.5 hover:bg-white/10"
                    aria-label="Refresh weather"
                    title="Refresh weather"
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 text-gray-200 ${loading ? "animate-spin" : ""}`}
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void getCurrentLocation();
                  }}
                  className="rounded-full p-1.5 hover:bg-white/10"
                  aria-label="Use device location"
                  title="Use device location"
                >
                  {isCurrentLocation && !isDefaultLocation ? (
                    <MapPinSolid className="h-5 w-5 text-primaryBlue" />
                  ) : isDefaultLocation ? (
                    <MapPinSolid className="h-5 w-5 text-amber-400" />
                  ) : (
                    <MapPinOutline className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-auto flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-end gap-4">
                <span className="text-6xl font-bold font-light leading-none tracking-tight text-white sm:text-7xl lg:text-8xl">
                  {tempF != null ? `${tempF}°` : "—"}
                </span>
                <div>
                  <div className="mb-2 rounded-xl border border-borderGray bg-primary/60 px-3 py-2 text-xs text-gray-200">
                  <div className="flex items-center gap-2 font-medium text-white">
                    <span>H</span>
                    <span>
                      {tempHighF != null ? `${Math.round(tempHighF)}°` : "—"}
                    </span>
                  </div>
                    
                  </div>
                  <div className="mb-2 rounded-xl border border-borderGray bg-primary/60 px-3 py-2 text-xs text-gray-200">
                  <div className="flex items-center gap-2 font-medium text-gray-400">
                    <span>L</span>
                    <span>
                    {tempLowF != null ? `${Math.round(tempLowF)}°` : "—"}
                    </span>
                  </div>
                  </div>
                </div>
              </div>
              {iconUrl && (
                <img
                  src={iconUrl}
                  alt=""
                  className="h-16 w-16 object-contain opacity-90 sm:h-20 sm:w-20"
                />
              )}
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-gray-200 sm:text-base">
              <MapPinOutline className="h-5 w-5 shrink-0 text-white sm:h-8 sm:w-8" />
              <span>
                {locationTitle || "—"}
                {locationSub ? `, ${locationSub}` : ""}
              </span>
            </div>
          </div>

          <SunriseSunsetArc
            sunrise={sunrise}
            sunset={sunset}
            timeZone={sunTimeZone}
            sunriseIso={sunriseIso}
            sunsetIso={sunsetIso}
            reduceMotion={reduceMotion}
            nowTick={nowTick}
            glassCardClassName="min-h-[280px] sm:col-span-2 lg:col-span-5"
          />

          {/* UV */}
          <div className={`${glassCard} min-w-0 sm:col-span-2 lg:col-span-7`}>
            <div className="flex items-center gap-2 text-gray-200">
              <IconImg
                src={sunIcon}
                alt=""
                className="brightness-0 invert"
              />
              <span className="font-medium text-white">UV Index</span>
            </div>
            <div className="mt-4 text-5xl font-light text-white">
              {uvi != null ? Math.round(uvi) : "—"}
            </div>
            {uvi == null && <ApiMissingNote className="mt-1 block" />}
            <div className="mt-6 h-3 w-full overflow-hidden rounded-full bg-primary/80">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-yellow-400 to-red-500 transition-[width] duration-700 ease-out"
                style={{ width: `${uvFillPct}%` }}
              />
            </div>
          </div>

          {/* Wind */}
          <div className={`${glassCard} min-w-0 sm:col-span-2 lg:col-span-5`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-gray-200">
                <IconImg
                  src={windIcon}
                  alt=""
                  className="brightness-0 invert"
                />
                <span className="font-medium text-white">Wind</span>
              </div>
              <div className="text-right text-2xl font-light text-white">
                {typeof windMph === "number" ? `${Math.round(windMph)} mph` : "—"}
                {windMph == null && (
                  <ApiMissingNote className="mt-1 block text-right" />
                )}
              </div>
            </div>
            <div className="mt-4 h-[72px] w-full">
              <svg
                viewBox="0 0 320 72"
                className="h-full w-full"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="windStroke"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.3" />
                    <stop
                      offset="50%"
                      stopColor="#e0f2fe"
                      stopOpacity="0.95"
                    />
                    <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.35" />
                  </linearGradient>
                </defs>
                {windPath ? (
                  <path
                    d={windPath}
                    fill="none"
                    stroke="url(#windStroke)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={
                      reduceMotion
                        ? "opacity-90"
                        : "animate-[wind-fade_1.4s_ease-out_forwards] opacity-0"
                    }
                  />
                ) : null}
              </svg>
            </div>
            {!windPath && (
              <p className="mt-1 text-xs text-gray-400">
                Hourly wind trend unavailable.{" "}
                <ApiMissingNote className="inline" />
              </p>
            )}
          </div>

          {/* Humidity */}
          <div className={`${glassCard} min-w-0 p-4 lg:col-span-3`}>
            <div className="flex items-center gap-2 text-gray-300">
            <IconImg
                  src={humidityIcon}
                  alt=""
                  className="brightness-0 invert w-6 h-6"
                />
              <span className="text-sm font-medium text-white">Humidity</span>
            </div>
            <div className="mt-3 text-3xl font-light text-white">
              {typeof humidity === "number" ? `${Math.round(humidity)}%` : "—"}
            </div>
            {humidity == null && <ApiMissingNote className="mt-1 block" />}
            <p className="mt-2 text-xs text-gray-400">
              {typeof humidity === "number" && humidity >= 40 && humidity <= 60
                ? "Humidity is good"
                : typeof humidity === "number"
                  ? humidity > 60
                    ? "Feels humid"
                    : "Quite dry"
                  : ""}
            </p>
          </div>

          {/* Visibility */}
          <div className={`${glassCard} min-w-0 p-4 lg:col-span-3`}>
            <div className="flex items-center gap-2 text-gray-300">
              <IconImg
                  src={visibilityIcon}
                  alt=""
                  className="brightness-0 invert w-6 h-6"
                />
              <span className="text-sm font-medium text-white">Visibility</span>
            </div>
            <div className="mt-3 text-3xl font-light text-white">
              {visMi != null ? `${visMi.toFixed(0)} mi` : "—"}
            </div>
            {visMi == null && <ApiMissingNote />}
            <p className="mt-2 text-xs text-gray-400">
              {obsFooter ? `As of ${obsFooter}` : (
                <>
                  <span className="text-gray-400">Observation time: </span>
                  <ApiMissingNote className="inline" />
                </>
              )}
            </p>
          </div>

          {/* Pressure */}
          <div className={`${glassCard} min-w-0 p-4 lg:col-span-3`}>
            <div className="flex items-center gap-2 text-gray-300">
            <IconImg
                  src={pressureIcon}
                  alt=""
                  className="brightness-0 invert w-6 h-6"
                />
              <span className="text-sm font-medium text-white">Pressure</span>
            </div>
            <div className="mt-3 text-3xl font-light text-white">
              {typeof pressure === "number" ? `${Math.round(pressure)} mb` : "—"}
            </div>
            {pressure == null && <ApiMissingNote />}
            <p className="mt-2 text-xs text-gray-400">
              {obsFooter ? `As of ${obsFooter}` : (
                <>
                  <span className="text-gray-400">Observation time: </span>
                  <ApiMissingNote className="inline" />
                </>
              )}
            </p>
          </div>

          {/* Precipitation chance — pop from forecast API */}
          <div className={`${glassCard} min-w-0 p-4 lg:col-span-3`}>
            <div className="flex items-center gap-2 text-gray-300">
              <CloudIcon className="h-6 w-6 shrink-0 text-white" />
              <span className="text-sm font-medium text-white">Precipitation</span>
            </div>
            <div className="mt-3 text-3xl font-light text-white">
              {pop != null ? `${pop}%` : "—"}
            </div>
            {pop == null && <ApiMissingNote className="mt-1 block" />}
            <p className="mt-2 text-xs text-gray-400">{popHint}</p>
          </div>
        </div>
      )}
      <style>{`
        @keyframes wind-fade {
          from { opacity: 0; filter: blur(2px); }
          to { opacity: 0.95; filter: blur(0); }
        }
      `}</style>
    </div>
  );
}
